import React from "react";
import { createRoot } from "react-dom/client";

import App from "./App";
import { AppErrorBoundary } from "./components/error-boundary/AppErrorBoundary";
import { isSentryEnabled } from "./utils/sentry";

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root not found!");
}
const root = createRoot(container);
if (!root) {
  throw new Error("Root couldn't be created!");
}

/**
 * Defer Sentry (incl. Session Replay) off the critical first-paint path.
 * Errors before init still hit AppErrorBoundary; Replay starts after load.
 */
async function initSentry(): Promise<void> {
  const [Sentry, SentryReact] = await Promise.all([
    import("@sentry/capacitor"),
    import("@sentry/react"),
  ]);

  Sentry.init(
    {
      Dist: "web",
      Release: `my-fire-plane@${import.meta.env.APP_VERSION}`,
      TracePropagationTargets: [
        "localhost",
        /^https:\/\/my-fire-plane-core\.pinthat\.dev/u,
      ],
      dsn: "https://07c30741deaa8c3d8beb74ea63cbf8f2@o4508913601413120.ingest.us.sentry.io/4508915005849600",
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      replaysOnErrorSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      tracesSampleRate: 1.0,
    },
    SentryReact.init,
  );
  Sentry.setTag("deployment", import.meta.env.MODE);
}

if (isSentryEnabled()) {
  initSentry().catch(() => {
    // Sentry failure must not block app boot.
  });
}

root.render(
  <React.StrictMode>
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  </React.StrictMode>,
);
